import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skull, LogIn, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await api.login(email, password);
      toast.success("Login successful");
      navigate("/admin");
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-demolition-black flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Skull className="w-16 h-16 text-demolition-red mx-auto mb-4" />
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            ADMIN LOGIN
          </h1>
          <p className="font-mono text-white/50 text-sm mt-2">
            Official Demolition Fan Page
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-demolition-paper border border-white/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-900/20 border border-red-500/30 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="font-mono text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="font-teko text-lg text-white uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@demolition.com"
                className="bg-demolition-subtle border-white/10 text-white font-mono placeholder:text-white/30 focus:border-demolition-red"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-teko text-lg text-white uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="bg-demolition-subtle border-white/10 text-white font-mono placeholder:text-white/30 focus:border-demolition-red"
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-demolition-red text-white font-teko text-xl uppercase tracking-widest hover:bg-demolition-red-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="animate-pulse">LOGGING IN...</span>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>LOGIN</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="font-mono text-white/50 text-sm hover:text-demolition-red transition-colors"
          >
            ← Back to site
          </a>
        </div>
      </div>
    </div>
  );
}
